import { useMediaQuery } from '@ltvco/refresh-lib/theme';
import { enqueueSnackbar } from '@ltvco/refresh-lib/v1';
import {
  isPhoneReportSearchData,
  isPersonReportSearchData,
  isPropertyReportSearchData,
  isSocialNetworkReportSearchData,
  isUsernameReportSearchData,
  isVehicleReportSearchData,
  useMarkReportChangesViewed,
  useReportChanges,
  type ReportMonitor,
} from '@ltvco/refresh-lib/v2';

import { MonitoringChangesPanel } from 'components/ChatBV/components/ReportLibrary/MonitoredReports/MonitoringChangesPanel';
import { getUnviewedChanges } from 'components/ChatBV/components/ReportLibrary/MonitoredReports/utils';

import { MonitoringColumnContainer } from './MonitoringColumn.styles';

const reportTypeLabels: Record<string, string> = {
  reverse_phone_report: 'Phone Report',
  detailed_person_report: 'Person Report',
  property_report: 'Property Report',
  social_network_report: 'Email Report',
  username_report: 'Username Report',
  vehicle_report: 'Vehicle Report',
  contact_report: 'Contact Report',
};

const toTitleCase = (str: string): string =>
  str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const getMonitorTitle = (monitor: ReportMonitor): string => {
  const data = monitor.search_data;
  if (!data) {
    return monitor.permalink;
  }

  if (isPhoneReportSearchData(data)) {
    return data.phone_number;
  }
  if (isPersonReportSearchData(data)) {
    return toTitleCase(`${data.first_name} ${data.last_name}`.trim());
  }
  if (isPropertyReportSearchData(data)) {
    return data.address
      .replace(/\bnull\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  if (isSocialNetworkReportSearchData(data)) {
    return data.email;
  }
  if (isUsernameReportSearchData(data)) {
    return data.username;
  }
  if (isVehicleReportSearchData(data)) {
    if (data.vin) {
      return data.vin;
    }
    return toTitleCase(`${data.year} ${data.make} ${data.model}`.trim());
  }

  return monitor.permalink;
};

export { reportTypeLabels, getMonitorTitle };

interface MonitoringColumnProps {
  monitor: ReportMonitor;
  currentWidth: string;
  onClose: () => void;
  onViewReport: () => void;
  onNewChat: () => void;
  isViewReportSupported?: boolean;
  viewReportDisabledTooltip?: string;
}

export const MonitoringColumn = ({
  monitor,
  currentWidth,
  onClose,
  onViewReport,
  onNewChat,
  isViewReportSupported = true,
  viewReportDisabledTooltip,
}: MonitoringColumnProps) => {
  const isMobileBreakpoint = useMediaQuery('(max-width: 1024px)');
  const { data: changes } = useReportChanges({
    permalink: monitor.permalink,
  });
  const resolvedChanges = changes ?? [];
  const { markReportChangesViewedMutation } = useMarkReportChangesViewed();

  const unviewedChanges = getUnviewedChanges(resolvedChanges);

  const handleMarkViewed = () => {
    if (
      markReportChangesViewedMutation.isLoading ||
      unviewedChanges.length === 0
    ) {
      return;
    }
    markReportChangesViewedMutation.mutate(
      unviewedChanges.map((change) => ({
        id: change.id,
        permalink: change.permalink,
      })),
      {
        onError: () => {
          enqueueSnackbar(
            'Couldn’t mark all updates as viewed. Please try again.',
            { variant: 'error' }
          );
        },
      }
    );
  };

  return (
    <MonitoringColumnContainer
      direction="column"
      spacing={0}
      isMobile={isMobileBreakpoint}
      minWidth={isMobileBreakpoint ? undefined : currentWidth}
      width={isMobileBreakpoint ? '100%' : '40%'}
      height={isMobileBreakpoint ? '100%' : '98%'}
    >
      <MonitoringChangesPanel
        reportTitle={getMonitorTitle(monitor)}
        reportType={
          reportTypeLabels[monitor.report_type] || monitor.report_type
        }
        lastUpdated={monitor.updated_at}
        changes={resolvedChanges}
        onClose={onClose}
        onViewReport={onViewReport}
        onNewChat={onNewChat}
        isViewReportSupported={isViewReportSupported}
        viewReportDisabledTooltip={viewReportDisabledTooltip}
        hasUnviewedChanges={unviewedChanges.length > 0}
        onMarkViewed={handleMarkViewed}
        isMarkingViewed={markReportChangesViewedMutation.isLoading}
      />
    </MonitoringColumnContainer>
  );
};
